import Papa from 'papaparse';

export const MAX_CSV_ROWS = 2000;
export const CSV_HEADERS = ['English', 'Chinese'];
export const DEFAULT_ERROR_PREVIEW_LIMIT = 10;
export const CSV_PAGE_SIZE = 20;

const normalizeCell = (value) => (typeof value === 'string' ? value.trim() : '');

const hasUnexpectedHeaders = (headers) => {
  const expected = new Set(CSV_HEADERS);
  return headers.some((header) => !expected.has(header));
};

export const buildCsvTemplateContent = () => {
  const lines = [
    CSV_HEADERS.join(','),
    '"technician centers","工程师驿站"',
  ];
  return `\uFEFF${lines.join('\n')}`;
};

export const parseAndValidateTerminologyCsv = (csvText, options = {}) => {
  const maxRows = Number.isInteger(options.maxRows) ? options.maxRows : MAX_CSV_ROWS;
  const errorPreviewLimit = Number.isInteger(options.errorPreviewLimit)
    ? options.errorPreviewLimit
    : DEFAULT_ERROR_PREVIEW_LIMIT;

  const parsed = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) => (typeof header === 'string' ? header.trim() : header),
  });

  const allErrors = [];
  const warnings = [];
  const entries = [];
  const seenKeys = new Map();

  if (parsed.errors.length > 0) {
    parsed.errors.forEach((error) => {
      allErrors.push({
        row: typeof error.row === 'number' ? error.row + 2 : '-',
        message: error.message || 'CSV parsing error',
      });
    });
  }

  const metaFields = Array.isArray(parsed.meta?.fields) ? parsed.meta.fields : [];
  const headerSet = new Set(metaFields);
  const missingHeaders = CSV_HEADERS.filter((header) => !headerSet.has(header));
  const unexpectedHeaders = hasUnexpectedHeaders(metaFields);

  if (missingHeaders.length > 0) {
    allErrors.push({
      row: '-',
      message: `Missing required headers: ${missingHeaders.join(', ')}`,
    });
  }

  if (unexpectedHeaders) {
    allErrors.push({
      row: '-',
      message: 'Header must only contain: English, Chinese',
    });
  }

  const rows = Array.isArray(parsed.data) ? parsed.data : [];

  if (rows.length === 0) {
    allErrors.push({
      row: '-',
      message: 'CSV has no data rows.',
    });
  }

  if (rows.length > maxRows) {
    allErrors.push({
      row: '-',
      message: `Row limit exceeded: ${rows.length} > ${maxRows}`,
    });
  }

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const key = normalizeCell(row.English);
    const value = normalizeCell(row.Chinese);

    if (!key || !value) {
      allErrors.push({
        row: rowNumber,
        message: 'English and Chinese are both required.',
      });
      return;
    }

    if (seenKeys.has(key)) {
      warnings.push({
        row: rowNumber,
        message: `Duplicate English key "${key}" ignored (keep first occurrence).`,
      });
      return;
    }

    seenKeys.set(key, rowNumber);
    entries.push({ key, value });
  });

  const errors = allErrors.slice(0, errorPreviewLimit);
  const droppedRows = rows.length - entries.length;

  return {
    ok: allErrors.length === 0,
    entries,
    errors,
    warnings,
    summary: {
      totalRows: rows.length,
      validRows: entries.length,
      droppedRows,
      duplicateKeys: warnings.length,
      hiddenErrorCount: Math.max(allErrors.length - errors.length, 0),
      errorCount: allErrors.length,
    },
  };
};

export const paginateEntries = (entries, currentPage, pageSize = CSV_PAGE_SIZE) => {
  const normalizedEntries = Array.isArray(entries) ? entries : [];
  const totalItems = normalizedEntries.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const page = Math.min(Math.max(1, currentPage || 1), totalPages);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    items: normalizedEntries.slice(startIndex, endIndex),
  };
};
